/**
 * World Events Service — browser-side event emission.
 *
 * The frontend is a dumb emitter. Call emitEvent(type, data) to fire a world
 * event. The backend constructs the full WorldEvent (id, ts, dataspaceId, actor)
 * from the JWT auth context — the frontend does not need to know the event schema.
 *
 * For tool-runtime → browser pushes (ui commands, job progress), the browser
 * uses EventSource connections to tool-runtime's SSE endpoints directly.
 */

import { API_BASE_URL } from '../api/config';

// ── Active chat thread tracker ────────────────────────────────────────────────
// Updated live by SpaceChat, read by sidebar on entity click.
let _activeChatSpaceId = '';
let _activeChatThreadId = '';

export function setActiveChatThread(spaceId: string, threadId: string): void {
  _activeChatSpaceId = spaceId;
  _activeChatThreadId = threadId;
  console.log(`[WorldEvents] setActiveChatThread: space=...${spaceId.slice(-6)} thread=...${threadId.slice(-8)}`);
}

export function getActiveChatThread(spaceId: string): string {
  return _activeChatSpaceId === spaceId ? _activeChatThreadId : '';
}

// ── Dataspace context resolution ──────────────────────────────────────────────
// Moved here from the deleted gui/src/services/worldEvents.ts.

export function getCurrentDataspaceId(): string {
  try {
    const { getCurrentDataspaceContext } = require('../stores/dataspaceContextStore');
    const context = getCurrentDataspaceContext();
    if (context.orgId) {
      return context.orgId;
    }
  } catch {
    // context store not available
  }
  // Store not yet synced — return empty string so the backend's server-side
  // resolution (tools_runtime.py reads current_dataspace_context from MongoDB)
  // fills in the correct org/dataspace ID. Returning a personal userId here
  // would cause team members to query the wrong (personal) dataspace edge set.
  return '';
}

// ── DOM event shape ───────────────────────────────────────────────────────────
// Used by same-session subscribers (e.g. useAmbientSuggestions).

export interface RiverDOMEvent {
  type: string;
  data: Record<string, any>;
  spaceId?: string;
}

// ── Emitter ───────────────────────────────────────────────────────────────────

/**
 * Emit a world event. The backend constructs the full WorldEvent
 * (id, ts, dataspaceId, actor) from the JWT auth context.
 * Fire-and-forget — never throws.
 */
export function emitEvent(
  type: string,
  data: Record<string, any> = {},
  spaceId?: string,
): void {
  // 1. DOM bus — same-session subscribers react without a server round-trip.
  try {
    window.dispatchEvent(new CustomEvent('river:worldevent', { detail: { type, data, spaceId } satisfies RiverDOMEvent }));
  } catch { /* non-fatal */ }

  // 2. REST POST — backend constructs the full WorldEvent, writes to MongoDB,
  //    and proxies to tool-runtime for AgentSupervisor dispatch.
  // Include dataspaceId from the current UI context so the backend doesn't need
  // to guess which org space the user is currently viewing.
  const authToken = localStorage.getItem('authToken') || '';
  const dataspaceId = getCurrentDataspaceId();
  fetch(`${API_BASE_URL}/world-events/emit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ type, data, spaceId, dataspaceId }),
  }).catch(() => {}); // Non-fatal

  try {
    console.log(
      `[WorldEvents] emitted ${type}` +
      (spaceId ? ` | space=...${spaceId.slice(-6)}` : '') +
      (Object.keys(data).length > 0 ? ` | data=${JSON.stringify(data).slice(0, 80)}` : ''),
    );
  } catch { /* non-fatal */ }
}

// ── Dev-mode debug helper ─────────────────────────────────────────────────────
// Usage from browser console:
//   window.__worldEvents.emit('integration.connected', { service: 'Gmail', serviceName: 'Gmail' })
//   window.__worldEvents.stats()  → calls GET /api/world-events/stats

if (typeof window !== 'undefined') {
  (window as any).__worldEvents = {
    emit(type: string, data: Record<string, any> = {}, spaceId?: string): void {
      emitEvent(type, data, spaceId);
      console.info('[WorldEvents] emitted:', { type, data, spaceId });
    },
    stats(): void {
      const authToken = localStorage.getItem('authToken') || '';
      fetch(`${API_BASE_URL}/world-events/stats`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
        .then(r => r.json())
        .then(d => console.info('[WorldEvents] stats:', d))
        .catch(e => console.warn('[WorldEvents] stats failed:', e));
    },
  };
}
